import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, change } from 'redux-form';
import { TextAreaField } from '../../../../../../components/fields';
import {
    getTimesheetShifts,
    approveTimesheet,
    rejectTimesheet,
    deleteTimeRecord,
    saveTimeEntry,
    resubmitTimesheet,
    confirmTimesheet,
    updateTimesheet,
    getJobProjects
} from '../../../../../../reducers/timekeeping';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { TIMESHEET_ACTIONS, TIMESHEET_STATUS_LOCKED, TIMESHEET_STATUS } from '../../../../../../text/enumerations';
import { timeObjectFromMinutes, timeObjectToMinutes } from '../../../../../../utilities/time';
import { RoleDefinitions } from '../../../../../../utilities/roles';
import { required } from '../../../../../../utilities/validation';
import WorkWeek from '../../../../components/workWeek';

class ContractorEditShiftsForm extends React.Component {
    constructor(props) {
        super(props);

        this.resolveActions = this.resolveActions.bind(this);

        this.reject = this.reject.bind(this);
        this.approve = this.approve.bind(this);
        this.submit = this.submit.bind(this);
        this.confirm = this.confirm.bind(this);
        this.resubmit = this.resubmit.bind(this);
        this.updateShifts = this.updateShifts.bind(this);
    }

    componentDidMount() {
        const { job, getJobProjects } = this.props;
        getJobProjects(job.id);
    }

    resolveActions() {
        const { action, cancel } = this.props;

        switch (action) {
            case TIMESHEET_ACTIONS.CONFIRM:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.submit} /> } },
                    submitAction: values => this.updateShifts(values, this.confirm)
                };

            case TIMESHEET_ACTIONS.EDIT:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.ok} /> } },
                    submitAction: values => this.updateShifts(values, this.resubmit)
                };

            case TIMESHEET_ACTIONS.VIEW:
            default:
                return { actions: { submit: { message: <FormattedMessage {...messages.ok} /> } }, submitAction: cancel };
        }
    }

    submit(values) {
        const { submitAction } = this.resolveActions();
        submitAction(values);
    }

    approve(values) {
        const { timesheet, approveTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            approveTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    reject(values) {
        const { timesheet, rejectTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            rejectTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    resubmit(values) {
        const { worker_notes } = values;
        const { timesheet, resubmitTimesheet, cancel, reloadData, updateTimesheet } = this.props;

        updateTimesheet({ ...timesheet, worker_notes }, () =>
            resubmitTimesheet(timesheet.id, () => {
                reloadData();
                cancel();
            })
        );
    }
    confirm(values) {
        const { worker_notes } = values;
        const { timesheet, confirmTimesheet, cancel, reloadData, updateTimesheet } = this.props;
        confirmTimesheet(timesheet.id, () =>
            updateTimesheet({ ...timesheet, worker_notes }, () => {
                reloadData();
                cancel();
            })
        );
    }

    updateShifts(values, onComplete) {
        const { shifts, job, project, saveTimeEntry } = this.props;

        let modified = [];
        shifts.forEach(s => {
            if (values[s.id] && timeObjectToMinutes(values[s.id].duration) !== s.minutes) {
                modified.push({ ...s, ...values[s.id] });
            }
        });

        if (modified.length === 0) {
            onComplete(values);
            return;
        }

        this.setState({ ...this.state, loading: modified.length }, () => {
            modified.forEach(s => {
                saveTimeEntry({ ...s, time_in: moment.tz(s.time_in, job.work_timezone), jobId: job.id, project_id: project.id, minutes: s.duration }, () =>
                    this.setState({ ...this.state, loading: this.state.loading - 1 }, () => {
                        if (this.state.loading === 0 && onComplete) {
                            onComplete(values);
                        }
                    })
                );
            });
        });
    }

    render() {
        const { role, job, shifts, projects, timesheet, handleSubmit, submitting, cancel } = this.props;
        if (!timesheet || !shifts || !job || !projects) return null;

        const { actions } = this.resolveActions();

        return (
            <div className="row">
                <div className="col-xs-12 group">
                    <div className="col-xs-12">
                        <WorkWeek date={timesheet.period_ending} job={job} timesheet={timesheet} readOnly={true} />
                    </div>
                </div>
                <form onSubmit={handleSubmit(values => this.submit(values))}>
                    {timesheet.approver_notes && (
                        <div className="col-xs-12">
                            <Field
                                {...{
                                    component: TextAreaField,
                                    placeholder: messages.approverNotes,
                                    name: 'approver_notes',
                                    disabled: true
                                }}
                            />
                        </div>
                    )}
                    {[TIMESHEET_STATUS.PENDING, TIMESHEET_STATUS.NOTAPPROVED].includes(timesheet.status) && (
                        <div className="col-xs-12">
                            <Field
                                {...{
                                    component: TextAreaField,
                                    placeholder: messages.notes,
                                    name: 'worker_notes',
                                    validate: role.role === RoleDefinitions.Contractor.type ? undefined : required,
                                    disabled: TIMESHEET_STATUS_LOCKED.includes(timesheet.status)
                                }}
                            />
                        </div>
                    )}
                    <div className="col-xs-12">
                        <WizardButtons disabled={submitting} cancel={{ action: cancel }} {...actions} />
                    </div>
                </form>
            </div>
        );
    }
}

ContractorEditShiftsForm.defaultProps = {
    action: undefined,
    job: undefined,
    timesheet: undefined,
    shifts: undefined,
    cancel: () => null,
    reloadData: () => null
};
ContractorEditShiftsForm.propTypes = {
    action: PropTypes.string.isRequired,
    job: PropTypes.object.isRequired,
    timesheet: PropTypes.object.isRequired,
    shifts: PropTypes.array.isRequired,
    cancel: PropTypes.func.isRequired,
    reloadData: PropTypes.func
};

ContractorEditShiftsForm = reduxForm({
    destroyOnUnmount: true,
    enableReinitialize: false,
    forceUnregisterOnUnmount: true,
    keepDirtyOnReinitialize: false
})(ContractorEditShiftsForm);

const mapStateToProps = (state, props) => {
    const { shifts, timesheet } = props;
    const projects = state.timekeeping.projects;

    let initialValues = undefined;
    if (shifts && shifts.length > 0 && [TIMESHEET_STATUS.PENDING, TIMESHEET_STATUS.NOTAPPROVED].includes(timesheet.status)) {
        initialValues = { worker_notes: timesheet.worker_notes, approver_notes: timesheet.approver_notes };
        shifts.forEach(shift => {
            initialValues[shift.id] = { duration: timeObjectFromMinutes(shift.minutes, false) };
        });
    }

    return {
        role: state.identity.role,
        projects,
        initialValues
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changeField: change,
            getJobProjects,
            getTimesheetShifts,
            approveTimesheet,
            rejectTimesheet,
            deleteTimeRecord,
            resubmitTimesheet,
            confirmTimesheet,
            updateTimesheet,
            saveTimeEntry
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ContractorEditShiftsForm);



// WEBPACK FOOTER //
// ./src/pages/timekeeping/forms/editShifts/tenants/contractor/index.jsx